import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import Table from "../common/Table";
import {
  PlusIcon,
  EyeIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useTermsAndConditionsInfinite } from "../../hooks/useTermsAndConditionsInfinite";
import {
  TermsAndConditions,
  TermsAndConditionsFormData,
} from "../../schemas/termsAndConditionsSchema";
import TermsAndConditionsForm from "../forms/TermsAndConditionsForm";
import Modal from "../common/Modal";
import { getFullMediaUrl } from "../../utils/mediaUtils";

const TermsAndConditionsPage: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState<TermsAndConditions | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    termsAndConditions,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    uploadTermsAndConditions,
  } = useTermsAndConditionsInfinite();

  const handleView = (terms: TermsAndConditions) => {
    setSelectedTerms(terms);
    setIsViewModalOpen(true);
  };

  const handleSubmit = async (data: TermsAndConditionsFormData) => {
    setIsSubmitting(true);
    try {
      const file = data.file?.[0];
      if (!file) {
        throw new Error("File is required");
      }
      await uploadTermsAndConditions({ file });
      setIsFormModalOpen(false);
    } catch (error) {
      // Error is handled by toast notification
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: ColumnDef<TermsAndConditions>[] = [
    {
      accessorKey: "fileName",
      header: "File Name",
      cell: ({ getValue }) => {
        const fileName = getValue() as string;
        return (
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-primary-default" />
            <span className="truncate max-w-xs">{fileName}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "version",
      header: "Version",
      cell: ({ getValue }) => {
        const version = getValue() as number;
        return (
          <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
            v{version}
          </span>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span
            className={`px-2 py-1 rounded text-sm font-medium ${
              isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      cell: ({ getValue }) => {
        const date = getValue() as string;
        return (
          <span className="text-sm text-gray-600">{formatDate(date)}</span>
        );
      },
    },
    {
      accessorKey: "updatedAt",
      header: "Updated At",
      cell: ({ getValue }) => {
        const date = getValue() as string;
        return (
          <span className="text-sm text-gray-600">{formatDate(date)}</span>
        );
      },
    },
  ];

  const actions = [
    {
      label: "View",
      icon: <EyeIcon className="h-4 w-4" />,
      onClick: handleView,
      variant: "primary" as const,
    },
  ];

  return (
    <div className="flex-1 m-4 p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Terms and Conditions
        </h1>
        <button
          onClick={() => {
            setIsFormModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-light transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Upload New Version
        </button>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Uploading a new Terms and Conditions file will
          automatically create a new version. The previous version will be
          marked as inactive.
        </p>
      </div>

      <Table
        columns={columns}
        data={termsAndConditions as TermsAndConditions[]}
        actions={actions}
        isLoading={isLoading}
        pagination
        searchable={false}
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />

      {isFormModalOpen && (
        <TermsAndConditionsForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
          }}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
        />
      )}

      {isViewModalOpen && selectedTerms && (
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedTerms(null);
          }}
          title={`Terms and Conditions - Version ${selectedTerms.version}`}
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  File Name
                </label>
                <p className="mt-1 text-sm text-gray-900 break-all">
                  {selectedTerms.fileName}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Version
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  v{selectedTerms.version}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Status
                </label>
                <p className="mt-1">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      selectedTerms.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {selectedTerms.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Created At
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(selectedTerms.createdAt)}
                </p>
              </div>
            </div>
            {selectedTerms.fileUrl && (
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Document
                </label>
                <div className="mt-2">
                  <a
                    href={getFullMediaUrl(selectedTerms.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-light transition-colors"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    View Document
                  </a>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TermsAndConditionsPage;
